import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import macroProcessApi, { MacroProcess } from '../../../../../api/macroProcess';
import typeMacroProcessApi, {
  TypeMacroProcess,
} from '../../../../../api/typeMacroProcess';
import { useAuth } from '../../../../../Auth/Auth';
import { sortTypeMacroProcessByName } from './utils';
import { filterOnlyActive } from '../utils';

type Props = {
  isEditing?: boolean;
  selectedRow?: MacroProcess;
  setSelectedRow?: React.Dispatch<
    React.SetStateAction<MacroProcess | undefined>
  >;
  fetchMacroProcesses?: () => Promise<void>;
};

const DEFAULT_TYPE_MACRO_PROCESS_ID = 0;
const DEFAULT_USER_ID = 1;

export const Form: React.FC<Props> = ({
  isEditing = false,
  selectedRow,
  setSelectedRow,
  fetchMacroProcesses,
}) => {
  const auth = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ok, setOk] = useState<boolean>(false);
  const [macroProcess, setMacroProcess] = useState<MacroProcess>({
    typeMacroProcessId: DEFAULT_TYPE_MACRO_PROCESS_ID,
    name: '',
    description: '',
    priority: null,
    weight: null,
    risks: '',
    metaNumerics: 0,
    mprResult: 0,
    lowerLimit: 0,
    upperLimit: 0,
    startValue: 0,
    endValue: 0,
    settingDate: null,
    validityPeriod: '',
    comments: '',
    isActive: 'SI',
    creationId: auth?.user?.id || DEFAULT_USER_ID,
  });
  const [typeMacroProcesses, setTypeMacroProcesses] = useState<
    TypeMacroProcess[] | []
  >([]);
  const [typeMacroProcessesError, setTypeMacroProcessesError] =
    useState<string>('');
  const [isTypeMacroProcessesLoading, setIsTypeMacroProcessesLoading] =
    useState<boolean>(true);

  const fetchTypeMacroProcesses = useCallback(async () => {
    setIsTypeMacroProcessesLoading(true);
    try {
      const responseTypeMacroProcesses =
        await typeMacroProcessApi.getTypeMacroProcesses();
      const typeMacroProcessesData = responseTypeMacroProcesses?.data ?? [];
      setTypeMacroProcesses(
        filterOnlyActive(sortTypeMacroProcessByName(typeMacroProcessesData))
      );
    } catch {
      setTypeMacroProcessesError(
        'Ha ocurrido un error al obtener los tipos de macro procesos.'
      );
    }
    setIsTypeMacroProcessesLoading(false);
  }, []);

  useEffect(() => {
    fetchTypeMacroProcesses();
    if (isEditing && setSelectedRow) {
      setSelectedRow(undefined);
      if (selectedRow) {
        setMacroProcess({
          id: selectedRow.id,
          typeMacroProcessId: selectedRow.typeMacroProcessId,
          name: selectedRow.name,
          description: selectedRow.description ?? '',
          priority: selectedRow.priority,
          weight: selectedRow.weight,
          risks: selectedRow.risks ?? '',
          metaNumerics: selectedRow.metaNumerics,
          mprResult: selectedRow.mprResult,
          lowerLimit: selectedRow.lowerLimit,
          upperLimit: selectedRow.upperLimit,
          startValue: selectedRow.startValue,
          endValue: selectedRow.endValue,
          settingDate: selectedRow.settingDate,
          validityPeriod: selectedRow.validityPeriod ?? '',
          comments: selectedRow.comments ?? '',
          isActive: selectedRow.isActive,
          creationId: auth?.user?.id || DEFAULT_USER_ID,
        });
      }
    }
  }, [
    auth?.user?.id,
    fetchTypeMacroProcesses,
    isEditing,
    selectedRow,
    setSelectedRow,
  ]);

  const handleOnClickSave = useCallback(async () => {
    setError('');
    setTypeMacroProcessesError('');
    const macroProcessId = macroProcess.id;
    setIsLoading(true);
    try {
      if (macroProcessId) {
        await macroProcessApi.updateMacroProcess(macroProcessId, macroProcess);
      } else {
        await macroProcessApi.createMacroProcess(macroProcess);
      }
      setOk(true);
    } catch {
      setError('Ha ocurrido un error al guardar el macro proceso.');
    }
    setMacroProcess({
      typeMacroProcessId: DEFAULT_TYPE_MACRO_PROCESS_ID,
      name: '',
      description: '',
      priority: null,
      weight: null,
      risks: '',
      metaNumerics: 0,
      mprResult: 0,
      lowerLimit: 0,
      upperLimit: 0,
      startValue: 0,
      endValue: 0,
      settingDate: null,
      validityPeriod: '',
      comments: '',
      isActive: 'SI',
      creationId: auth?.user?.id || DEFAULT_USER_ID,
    });
    if (isEditing && setSelectedRow) {
      setSelectedRow(undefined);
    }
    setIsLoading(false);
    if (fetchMacroProcesses) {
      await fetchMacroProcesses();
    }
  }, [
    macroProcess,
    auth?.user?.id,
    isEditing,
    setSelectedRow,
    fetchMacroProcesses,
  ]);

  const isTypeMacroProcessesIdInError =
    isEditing &&
    !typeMacroProcesses.some(
      (typeMacroProcess) =>
        typeMacroProcess?.id === macroProcess.typeMacroProcessId
    );

  return (
    <>
      <Box
        component='form'
        sx={{
          '& .MuiTextField-root': { m: 1, width: '40ch' },
        }}
        noValidate
        autoComplete='off'
      >
        <TextField
          select
          required
          label='Tipo de Macro Proceso'
          helperText='Tipo de Macro Proceso'
          value={
            !typeMacroProcesses.length ||
            !macroProcess.typeMacroProcessId ||
            isTypeMacroProcessesIdInError
              ? ''
              : macroProcess.typeMacroProcessId
          }
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              typeMacroProcessId: e.target.value as unknown as number,
            }))
          }
          error={
            !!!macroProcess.typeMacroProcessId || isTypeMacroProcessesIdInError
          }
        >
          {typeMacroProcesses.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type=''
          required
          label='Nombre'
          helperText='Nombre'
          value={macroProcess.name ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              name: e.target.value,
            }))
          }
          error={!!!macroProcess.name}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          type=''
          label='Descripción'
          helperText='Descripción'
          value={macroProcess.description ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              description: e.target.value,
            }))
          }
        />
        <TextField
          type='number'
          label='Prioridad'
          helperText='Prioridad'
          value={macroProcess.priority ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              priority: e.target.value as unknown as number,
            }))
          }
        />
        <TextField
          type='number'
          label='Peso'
          helperText='Peso'
          value={macroProcess.weight ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              weight: e.target.value as unknown as number,
            }))
          }
          inputProps={{ maxLength: 17 }}
        />
        <TextField
          type=''
          label='Riesgos'
          helperText='Riesgos'
          value={macroProcess.risks ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              risks: e.target.value,
            }))
          }
        />
        <TextField
          type='number'
          required
          label='Meta Numérica'
          helperText='Meta Numérica'
          value={macroProcess.metaNumerics ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              metaNumerics: e.target.value as unknown as number,
            }))
          }
          error={
            macroProcess.metaNumerics !== 0 && !!!macroProcess.metaNumerics
          }
          inputProps={{ maxLength: 17 }}
        />
        <TextField
          type='number'
          required
          label='Resultado Macro Proceso'
          helperText='Resultado Macro Proceso'
          value={macroProcess.mprResult ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              mprResult: e.target.value as unknown as number,
            }))
          }
          error={macroProcess.mprResult !== 0 && !!!macroProcess.mprResult}
          inputProps={{ maxLength: 17 }}
        />
        <TextField
          type='number'
          required
          label='Límite Inferior'
          helperText='Límite Inferior'
          value={macroProcess.lowerLimit ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              lowerLimit: e.target.value as unknown as number,
            }))
          }
          error={macroProcess.lowerLimit !== 0 && !!!macroProcess.lowerLimit}
          inputProps={{ maxLength: 17 }}
        />
        <TextField
          type='number'
          required
          label='Límite Superior'
          helperText='Límite Superior'
          value={macroProcess.upperLimit ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              upperLimit: e.target.value as unknown as number,
            }))
          }
          error={macroProcess.upperLimit !== 0 && !!!macroProcess.upperLimit}
          inputProps={{ maxLength: 17 }}
        />
        <TextField
          type='number'
          required
          label='Star Value'
          helperText='Star Value'
          value={macroProcess.startValue ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              startValue: e.target.value as unknown as number,
            }))
          }
          error={macroProcess.startValue !== 0 && !!!macroProcess.startValue}
          inputProps={{ maxLength: 17 }}
        />
        <TextField
          type='number'
          required
          label='End Value'
          helperText='End Value'
          value={macroProcess.endValue ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              endValue: e.target.value as unknown as number,
            }))
          }
          error={macroProcess.endValue !== 0 && !!!macroProcess.endValue}
          inputProps={{ maxLength: 17 }}
        />
        <DatePicker
          sx={{
            '& .Mui-error': { color: '#00000099 !important' },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#00000099 !important',
            },
          }}
          label='Fecha de Parametrización'
          slotProps={{
            textField: {
              helperText: 'DD/MM/AAAA',
            },
          }}
          value={dayjs(macroProcess.settingDate ?? '')}
          onChange={(newValue) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              settingDate: newValue
                ? dayjs(newValue).format('YYYY-MM-DD')
                : null,
            }))
          }
        />
        <TextField
          type=''
          label='Período de Validez'
          helperText='Período de Validez'
          value={macroProcess.validityPeriod ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              validityPeriod: e.target.value,
            }))
          }
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          type=''
          label='Observaciones'
          helperText='Observaciones'
          value={macroProcess.comments ?? ''}
          onChange={(e) =>
            setMacroProcess((prevState) => ({
              ...prevState,
              comments: e.target.value,
            }))
          }
        />
        {isEditing && (
          <TextField
            select
            required
            label='Activo'
            value={macroProcess.isActive}
            onChange={(e) =>
              setMacroProcess((prevState) => ({
                ...prevState,
                isActive: e.target.value,
              }))
            }
          >
            {[
              { value: 'SI', label: 'SI' },
              { value: 'NO', label: 'NO' },
            ].map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      <Button
        variant='contained'
        sx={{ textTransform: 'none' }}
        onClick={handleOnClickSave}
        disabled={
          isLoading ||
          isTypeMacroProcessesLoading ||
          !!!macroProcess.typeMacroProcessId ||
          !!!macroProcess.name ||
          (macroProcess.metaNumerics !== 0 && !!!macroProcess.metaNumerics) ||
          (macroProcess.mprResult !== 0 && !!!macroProcess.mprResult) ||
          (macroProcess.lowerLimit !== 0 && !!!macroProcess.lowerLimit) ||
          (macroProcess.upperLimit !== 0 && !!!macroProcess.upperLimit) ||
          (macroProcess.startValue !== 0 && !!!macroProcess.startValue) ||
          (macroProcess.endValue !== 0 && !!!macroProcess.endValue) ||
          isTypeMacroProcessesIdInError ||
          !!!macroProcess.isActive ||
          (isEditing && !!!macroProcess.id)
        }
      >
        {isLoading || isTypeMacroProcessesLoading ? (
          <CircularProgress size={25} />
        ) : (
          'Guardar'
        )}
      </Button>
      {(error || typeMacroProcessesError) && (
        <Typography color='red'>{error || typeMacroProcessesError}</Typography>
      )}
      {ok && (
        <Typography marginTop={'10px'}>
          El macro proceso se ha guardado correctamente.
        </Typography>
      )}
    </>
  );
};
